// stores/auth.ts
import { defineStore } from 'pinia';
import { external } from '../apis/external';
import { jwtDecode } from 'jwt-decode';
import {ProfileResult} from '../types/external';

export const useAuthStore = defineStore('authStorage', {
	state: () => ({
		profile: {} as ProfileResult | null,
		token: '' as string | null,
		isLoading: false,
		error: '' as string | null,
		isLoggedIn: false,
	}),

	actions: {
		async login(payload: { username: string; password: string }) {
			this.isLoading = true;
			this.error = null;

			try {
				const resp = await external.login(payload);
				if (resp.code !== '0') throw new Error(resp.message);
				if (!resp.result.token) throw new Error('ไม่พบ token');

				this.setData({ token: resp.result.token });
				await this.fetchData();
				this.updateIsLoggedIn();
			} catch (error: any) {
				this.error = error || 'เกิดข้อผิดพลาด';
				throw error;
			} finally {
				this.isLoading = false;
			}
		},

		setData({ token }: { token: string }) {
			this.token = token;
			localStorage.setItem('auth_token', token);
			this.updateIsLoggedIn();
		},

		hydrate() {
			this.token = localStorage.getItem('auth_token');
			this.updateIsLoggedIn();

			const raw = localStorage.getItem('auth_user');
			if (raw && raw !== 'undefined' && raw !== 'null') {
				try {
					this.profile = JSON.parse(raw);
				} catch {
					this.profile = null;
				}
			}
		},

		async logout() {
			// ยิง /logout ก่อนล้าง session — request interceptor ดึง token จาก store
			// ตอนส่ง request ถ้าล้างก่อนจะไม่มี Authorization header ติดไป server จึง
			// revoke session ไม่ได้ (ผู้ใช้เห็นว่าออกแล้ว แต่ token เดิมยังใช้ได้ต่อ)
			try {
				const resp = await external.logout({ data: null });
				if (resp.code !== '0') throw new Error(resp.message);
			} catch {
				// ignore — token อาจถูก revoke หรือหมดอายุไปแล้ว
			} finally {
				// ล้าง session เสมอ แม้ /logout จะ fail
				this.clearSession();
			}
		},

		/** ลบ session ฝั่ง client ทันทีโดยไม่เรียก API (ใช้ตอน 401) */
		clearSession() {
			this.profile = null;
			this.token = null;
			localStorage.removeItem('auth_token');
			localStorage.removeItem('auth_user');
			this.updateIsLoggedIn();
		},

		/** alias เดิมของ clearSession() — คงไว้ให้ call site เก่าและ patch ที่ fanout
		 *  ข้ามธีมเรียกได้ทั้งสองชื่อ โค้ดใหม่ให้ใช้ clearSession() */
		forceLogout() {
			this.clearSession();
		},

		updateIsLoggedIn() {
			try {
				const decoded: any = this.token ? jwtDecode(this.token) : null;
				this.isLoggedIn = !!decoded && !decoded.data?.isExpire;
			} catch {
				this.isLoggedIn = false;
			}
		},

		async fetchData() {
			this.isLoading = true;
			this.error = null;

			try {
				const resp = await external.profile({ data: null });
				if (resp.code === '4010') {
					this.forceLogout();
					throw new Error('เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่');
				}

				if (resp.code === '8017' || resp.code === '9902') {
					this.forceLogout();
					throw new Error(resp.message);
				}

				if (resp.code !== '0') throw new Error(resp.message);

				this.profile = resp.result;
				localStorage.setItem('auth_user', JSON.stringify(resp.result));
			} catch (error: any) {
				this.error = error || 'เกิดข้อผิดพลาด';
				throw error;
			} finally {
				this.isLoading = false;
			}
		},
	},
});
